import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useQuery } from '@tanstack/react-query';
import { theme } from '../styles/theme';
import PageLayout from '../components/layout/PageLayout';
import { getUserProfile } from '../api/auth';
import { formatDateTime } from '../utils/formatting';

const ProfilePage = () => {
  const { data: profile, isLoading, error } = useQuery({
    queryKey: ['profile'],
    queryFn: getUserProfile,
  });
  
  if (isLoading) {
    return (
      <PageLayout>
        <LoadingText>Lade Profil...</LoadingText>
      </PageLayout>
    );
  }
  
  if (error) {
    return (
      <PageLayout>
        <ErrorMessage>
          Fehler beim Laden des Profils. Bitte versuchen Sie es später erneut.
        </ErrorMessage>
      </PageLayout>
    );
  }
  
  return (
    <PageLayout>
      <Container>
        <Title>Ihr Profil</Title>
        
        <ProfileCard>
          <ProfileHeader>
            <Avatar>{getInitials(profile.firstName, profile.lastName)}</Avatar>
            <ProfileName>
              {profile.firstName} {profile.lastName}
            </ProfileName>
            <ProfileRole>
              {profile.role === 'admin' ? 'Administrator' : 'Benutzer'}
            </ProfileRole>
          </ProfileHeader>
          
          <ProfileInfo>
            <InfoGroup>
              <InfoLabel>Benutzername</InfoLabel>
              <InfoValue>{profile.username}</InfoValue>
            </InfoGroup>
            
            <InfoGroup>
              <InfoLabel>E-Mail</InfoLabel>
              <InfoValue>{profile.email}</InfoValue>
            </InfoGroup>
            
            {profile.lastLogin && (
              <InfoGroup>
                <InfoLabel>Letzte Anmeldung</InfoLabel>
                <InfoValue>{formatDateTime(profile.lastLogin)}</InfoValue>
              </InfoGroup>
            )}
            
            {profile.createdAt && (
              <InfoGroup>
                <InfoLabel>Konto erstellt am</InfoLabel>
                <InfoValue>{formatDateTime(profile.createdAt)}</InfoValue>
              </InfoGroup>
            )}
          </ProfileInfo>
        </ProfileCard>
        
        <Note>
          Die Passwortänderung wird in einer zukünftigen Version verfügbar sein.
        </Note>
      </Container>
    </PageLayout>
  );
};

// Helper function to get initials from name
const getInitials = (firstName, lastName) => {
  return `${firstName?.charAt(0) || ''}${lastName?.charAt(0) || ''}`.toUpperCase();
};

const Container = styled.div`
  padding: 1rem 0;
  max-width: 800px;
  margin: 0 auto;
`;

const Title = styled.h1`
  font-size: 1.5rem;
  margin-bottom: 1.5rem;
  color: ${theme.colors.heading};
`;

const ProfileCard = styled.div`
  background-color: ${theme.colors.surface};
  border-radius: ${theme.borderRadius.lg};
  box-shadow: ${theme.boxShadow.md};
  overflow: hidden;
`;

const ProfileHeader = styled.div`
  background-color: ${theme.colors.primary}10;
  padding: 2rem;
  text-align: center;
  border-bottom: 1px solid ${theme.colors.border};
`;

const Avatar = styled.div`
  width: 100px;
  height: 100px;
  border-radius: 50%;
  background-color: ${theme.colors.primary};
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 2rem;
  font-weight: 600;
  margin: 0 auto 1rem;
`;

const ProfileName = styled.h2`
  font-size: 1.5rem;
  margin-bottom: 0.5rem;
  color: ${theme.colors.heading};
`;

const ProfileRole = styled.div`
  font-size: 0.875rem;
  color: ${theme.colors.textLight};
  background-color: ${({ role }) => 
    role === 'admin' ? `${theme.colors.secondary}20` : `${theme.colors.info}20`};
  display: inline-block;
  padding: 0.25rem 0.75rem;
  border-radius: ${theme.borderRadius.full};
`;

const ProfileInfo = styled.div`
  padding: 2rem;
`;

const InfoGroup = styled.div`
  margin-bottom: 1.5rem;
  
  &:last-child {
    margin-bottom: 0;
  }
`;

const InfoLabel = styled.div`
  font-size: 0.875rem;
  font-weight: 600;
  color: ${theme.colors.textLight};
  margin-bottom: 0.25rem;
`;

const InfoValue = styled.div`
  font-size: 1rem;
  color: ${theme.colors.text};
`;

const LoadingText = styled.div`
  padding: 2rem;
  text-align: center;
  color: ${theme.colors.textLight};
`;

const ErrorMessage = styled.div`
  padding: 1rem;
  background-color: ${theme.colors.danger}10;
  color: ${theme.colors.danger};
  border-radius: ${theme.borderRadius.md};
  margin-bottom: 1rem;
`;

const Note = styled.div`
  margin-top: 2rem;
  padding: 1rem;
  background-color: ${theme.colors.info}10;
  border-radius: ${theme.borderRadius.md};
  color: ${theme.colors.textLight};
  font-size: 0.875rem;
  text-align: center;
`;

export default ProfilePage;
